/**
 * Custom hook for managing batch operation state
 * Handles batch status tracking, notifications, and state updates
 */

import { useState, useEffect, useCallback } from 'react'
import { BatchStatus } from '../types/epic-types'
import { BatchQueueRequest } from '@jirax/common'
import { useBatchStatus, getBatchRefetchInterval, isBatchActive } from './epicApi'

interface BatchStateOptions {
  onBatchComplete?: (batchId: string, success: boolean) => void
  onBatchError?: (batchId: string, error: Error) => void
  enableNotifications?: boolean
}

interface BatchStateReturn {
  activeBatch: BatchStatus | null
  isProcessing: boolean
  error: string | null
  startBatch: (batchId: string) => void
  clearBatch: () => void
  clearError: () => void
}

/**
 * Hook to manage batch operation state with real-time updates
 */
export function useBatchState(options: BatchStateOptions = {}): BatchStateReturn {
  const {
    onBatchComplete,
    onBatchError,
    enableNotifications = true
  } = options

  const [activeBatchId, setActiveBatchId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previousStatus, setPreviousStatus] = useState<string | null>(null)

  // Query batch status when we have an active batch
  const batchStatusQuery = useBatchStatus({
    batchId: activeBatchId || '',
    enabled: !!activeBatchId,
    refetchInterval: activeBatchId ? getBatchRefetchInterval(previousStatus || 'processing') || undefined : undefined
  })

  // Transform batch status data
  const activeBatch: BatchStatus | null = batchStatusQuery.data?.success ? {
    batchId: batchStatusQuery.data.batchId,
    status: batchStatusQuery.data.data.status,
    progress: {
      completed: batchStatusQuery.data.data.summary.completed + batchStatusQuery.data.data.summary.failed,
      total: batchStatusQuery.data.data.summary.totalEpics,
      percentage: batchStatusQuery.data.data.metrics.progressPercentage
    },
    estimatedTimeRemaining: batchStatusQuery.data.data.metrics.estimatedTimeRemaining,
    metrics: {
      processingRate: batchStatusQuery.data.data.metrics.processingRate,
      averageProcessingTime: batchStatusQuery.data.data.metrics.averageProcessingTime,
      successRate: batchStatusQuery.data.data.metrics.successRate,
      elapsedTime: batchStatusQuery.data.data.metrics.elapsedTime
    }
  } : null

  // Handle batch status changes
  useEffect(() => {
    if (!activeBatch || !previousStatus) {
      if (activeBatch?.status) {
        setPreviousStatus(activeBatch.status)
      }
      return
    }

    const currentStatus = activeBatch.status

    // Check for status transitions
    if (previousStatus !== currentStatus) {
      setPreviousStatus(currentStatus)

      // Handle batch completion
      if (previousStatus && isBatchActive(previousStatus) && currentStatus === 'completed') {
        if (enableNotifications) {
          showSuccessNotification(`Batch analysis completed successfully! Analyzed ${activeBatch.progress.completed} epics.`)
        }
        if (onBatchComplete) {
          onBatchComplete(activeBatch.batchId, true)
        }
        // Clear active batch after a delay to show completion state
        setTimeout(() => {
          setActiveBatchId(null)
          setPreviousStatus(null)
        }, 3000)
      }

      // Handle batch failure
      if (previousStatus && isBatchActive(previousStatus) && currentStatus === 'failed') {
        const errorMessage = `Batch analysis failed. ${activeBatch.progress.completed} of ${activeBatch.progress.total} epics were processed.`
        if (enableNotifications) {
          showErrorNotification(errorMessage)
        }
        if (onBatchError) {
          onBatchError(activeBatch.batchId, new Error(errorMessage))
        }
        // Clear active batch after a delay to show failure state
        setTimeout(() => {
          setActiveBatchId(null)
          setPreviousStatus(null)
        }, 5000)
      }
    }
  }, [activeBatch?.status, previousStatus, activeBatch?.batchId, activeBatch?.progress, onBatchComplete, onBatchError, enableNotifications])

  // Handle batch query errors
  useEffect(() => {
    if (batchStatusQuery.error) {
      const errorMessage = `Failed to fetch batch status: ${(batchStatusQuery.error as Error).message}`
      setError(errorMessage)
      if (onBatchError && activeBatchId) {
        onBatchError(activeBatchId, batchStatusQuery.error as Error)
      }
    } else {
      setError(null)
    }
  }, [batchStatusQuery.error, activeBatchId, onBatchError])

  // Start tracking a new batch
  const startBatch = useCallback((batchId: string) => {
    setActiveBatchId(batchId)
    setPreviousStatus(null)
    setError(null)
  }, [])

  // Clear current batch
  const clearBatch = useCallback(() => {
    setActiveBatchId(null)
    setPreviousStatus(null)
    setError(null)
  }, [])

  // Clear error state
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const isProcessing = activeBatch ? isBatchActive(activeBatch.status) : false

  return {
    activeBatch,
    isProcessing,
    error,
    startBatch,
    clearBatch,
    clearError
  }
}

/**
 * Show success notification using the notification system
 * Falls back to console.log if notification context is not available
 */
function showSuccessNotification(message: string) {
  // Try to use notification system if available
  try {
    const event = new CustomEvent('jirax-notification', {
      detail: { type: 'success', title: 'Batch Analysis Complete', message }
    })
    window.dispatchEvent(event)
  } catch {
    // Fallback to console
    console.log('SUCCESS:', message)
  }
}

/**
 * Show error notification using the notification system
 * Falls back to console.error if notification context is not available
 */
function showErrorNotification(message: string) {
  // Try to use notification system if available
  try {
    const event = new CustomEvent('jirax-notification', {
      detail: { type: 'error', title: 'Batch Analysis Failed', message }
    })
    window.dispatchEvent(event)
  } catch {
    // Fallback to console
    console.error('ERROR:', message)
  }
}

/**
 * Hook for managing batch queue requests with validation
 */
export function useBatchQueue() {
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const validateBatchRequest = useCallback((request: BatchQueueRequest): boolean => {
    setIsValidating(true)
    setValidationError(null)

    try {
      // Validate required fields
      if (!request.projectKey) {
        throw new Error('Project key is required')
      }

      if (!request.startDate || !request.endDate) {
        throw new Error('Start date and end date are required')
      }

      // Validate date format (basic check)
      const startDate = new Date(request.startDate)
      const endDate = new Date(request.endDate)

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Invalid date format')
      }

      // Validate date range
      if (startDate >= endDate) {
        throw new Error('Start date must be before end date')
      }

      // Validate date range is not too large (e.g., more than 2 years)
      const daysDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      if (daysDiff > 730) { // 2 years
        throw new Error('Date range cannot exceed 2 years')
      }

      return true
    } catch (error) {
      setValidationError((error as Error).message)
      return false
    } finally {
      setIsValidating(false)
    }
  }, [])

  const clearValidationError = useCallback(() => {
    setValidationError(null)
  }, [])

  return {
    validateBatchRequest,
    isValidating,
    validationError,
    clearValidationError
  }
}